/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import ErrorLevel from './core/error/error-level';

export default {
  // Identifikátor aplikace.
  name: 'player-lighter',
  // Výchozí hodnota deaktivace preventivní restartu.
  // Pokud je "true", ostatní preventivní restart se neprování
  // a ostatní nastavení se ignorují.
  // Použije se v případě, že toto nastavení není vyplněno na serveru.
  autorebootDefaultDisabled: false,
  // Výchozí interval, ve kterém se zařízení preventivně restartuje. [ms]
  // Použije se v případě, že toto nastavení není vyplněno na serveru.
  autorebootDefaultInterval: 2 * 24 * 60 * 60 * 1000,
  // Minimální doba čekání na automatický restart zařízení.
  // Příliš nízká hodnota může způsobit zacyklení zařízení v nekonečné
  // smyčce restrartů.
  // Příliš vysoká hodnota znesnadní E2E testy dlouhým čekáním.
  autorebootMinInterval: 15 * 60 * 1000,
  // Maximální délka intervalu, ve kterém se vyhledávají události. [s]
  // Jde o hotfix problémů s výkonem plánování.
  // Pokud plánovač hledá aktuální / následující / předcházející událost
  // vzhledem k aktuálnímu času, omezí interval na tuto hodnotu.
  // Pokud by bylo zařízení spuštěno po dobu delší, než je tato hodnota
  // a v plánování by zrovna v této době nebyla žádná událost,
  // tak se již další případná událost nespustí.
  // Nastavená hodnota souvisí s dalším omezením časovačů, které je popsáno
  // u nastavení `longTimersMaxDelay`.
  // Zde se použila stejná hodnota pouze převedená na sekundy.
  contentTaskMaxInterval: Math.ceil((Math.pow(2, 31) - 1) / 1000),
  // Maximální doba čekání na Cordova deviceready. [ms]
  // Hodnota pochází z Cordova implementace, která provádí vlastní kontrolu
  // a výpis do konzole.
  cordovaBootDelay: 5 * 1000,
  // Zařízení.
  device: {
    // Stahovač souborů.
    downloader: {
      // Výchozí velikost souboru, pro kterou se má uvolnit místo. [B]
      defaultSize: 64 * 1024 * 1024
    },
    // Komunikátor se serverem.
    communicator: {
      // Výchozí doba čekání před novým pokusem o synchronizaci. [ms]
      syncDelayBase: 60 * 1000
      // Maximální doba čekání před novým pokusem o synchronizaci po neúspěchu. [ms]
      // TODO #5 Zatím deaktivováno. Zpracovat dle nové logiky.
      // syncDelayMax: 4 * 60 * 1000,
      // Násobitel doby čekání před novým pokusem při neúspěchu.
      // TODO #5 Zatím deaktivováno. Zpracovat dle nové logiky.
      // syncDelayMultiplier: 2
    },
    // Sezení - různé konfigurace zařízení.
    session: {
      // Identifikátor session, která se načte při startu aplikace.
      defaultId: 'default'
    }
  },
  // Firmware zařízení.
  firmware: {
    // Výchozí velikost firmware, pro kterou se má uvolnit místo. [B]
    defaultSize: 64 * 1024 * 1024
  },
  // Maximální doba čekání na načtení iframe. [ms]
  htmlIframeLoadDelay: 60 * 1000,
  // Maximální počet plánování opakovaných událostí, který je nutný
  // pro prevenci zacyklení a zkrácení doby výpočtu v kalendáři.
  // Omezuje se počet opakování, nikoliv doba, po kterou se opakování provádí.
  // Nejmenší interval opakování je jeden den, z čehož lze při výpočtu vycházet.
  // Ostatní druh opakování (týdenní, ...) se budou opakovat po delší dobu.
  // Taktéž opakování ve větším intervalu (2 dny, ...) nebudou tolik omezené.
  // Výchozí hodnotou IcalExpander je 1000 opakování, tedy cca. 3 roky.
  // Na serveru je kalendář v rozsahu +-5 let od aktuálního data
  // tzn. rozsah 10 let, což je v nejhorším případě 10 * 365 opakování.
  // Aktuálně se aplikuje pouze u médiaplánu.
  // Plánování obsahových úloh je optimalizované a omezení není potřeba.
  icalMaxIterations: 5 * 365,
  // Rozsah verzí Ki-Wi Lighter API se kterými je aplikace kompatibilní.
  // Je založeno na semver specifikaci a lze využít její syntaxi.
  // Typicky se používá syntaxe '^1.3.7', což znamená "kompatibilní s 1.3.7"
  // a přesně to odpovídá rozsahu verzí >=1.3.7 a <2.0.0.
  kiwiLighterApiVersion: '0.11.*',
  // Maximální doba, na kterou je možné nastavit časovač.
  // Hodnota odpovídá maximu pro 32 bitový integer a vychází z implementace,
  // kterou používá většina rozšířených prohlížečů.
  longTimersMaxDelay: Math.pow(2, 31) - 1,
  // Maximální doba čekání na navázání komunikace s PDF prohlížečem. [ms]
  pdfConnectionTimeout: 5 * 1000,
  // Počet stránek PDF dokumentu, které se přehrají za jednu vteřinu. [Hz]
  // Na základě této hodnoty se určuje výchozí rychlost přehrávání PDF dokumentu.
  pdfPageRate: 0.1,
  // Maximální počet současně zobrazovaných PDF.
  pdfSourceMax: 3,
  // Platformně specifická nastavení.
  platform: {
    tizenSssp4: {
      // Minimální počet sekund od začátku epochy, po kterém se aplikace může spustit. [s]
      booterClockLimit: new Date(2000, 0).getTime(),
      // Interval, ve kterém se kontroluje, jestli je již prostředí připraveno
      // pro start aplikace. [ms]
      // Souvisí s Tizen bugem, kdy někdy není dostupný aktuální čas (viz #230).
      booterRetryDelay: 500,
      // Prodleva před synchronizací globálních a B2B hodin. [ms]
      // Televize Samsung obsahují dvoje hodiny -- jedny globální, ke kterým se
      // přistupuje pomocí standardního JavaScriptového API (`Date` apod.), druhé
      // v rámci B2B API. Tyto dva zdroje nejsou synchronizované a B2B hodiny se
      // navíc při provozu samovolně zpožďují. Časovače zapnutí a vypnutí se řídí
      // právě B2B hodinami, proto je naším niterným zájmem, aby byly během provozu
      // aktuální a co nejméně pozadu.
      clockSyncInterval: 24 * 60 * 60 * 1000
    }
  },
  // Úroveň důležitosti chyby, od které se začínají odesílat události na Sentry.
  // Chyby s nižší prioritou jsou zalogovány jako breadcrumbs a jsou odeslány
  // pouze jako dodatečná informace k nějaké budoucí chybě, která bude mít
  // stejnou nebo vyšší prioritu, než je zde stanovená úroveň.
  sentryCaptureErrorLevel: ErrorLevel.Error,
  // Maximální počet perzistentně uložených výjimek v offline stavu.
  // Je nutné nastavit na nízkou hodnotu, jinak hrozí zaplnění localStorage.
  sentryMaxEvents: 5,
  // Setup Wizard pro instalaci zařízení.
  setup: {
    // Doba mezi pokusy o automatickou registraci na základě otisku. [ms]
    automaticDelay: 60 * 1000,
    // Výchozí hodnota formulářového pole s názvem zařízení.
    deviceName: '',
    // Výchozí doba čekání před novým pokusem při neúspěchu. [ms]
    failureDelayBase: 60 * 1000,
    // Maximální doba čekání před novým pokusem po neúspěchu. [ms]
    failureDelayMax: 4 * 60 * 1000,
    // Násobitel doby čekání před novým pokusem při neúspěchu.
    failureDelayMultiplier: 2,
    // Výchozí hodnota formulářového pole s heslem uživatele.
    password: '',
    // Výchozí hodnota formulářového pole s URL serveru.
    serverUrl: 'https://server.ki-wi.cz/kiwi-server',
    // Doba čekání při zobrazení obazovky o úspěšnén nastavení. [ms]
    successDelay: 5 * 1000,
    // Výchozí hodnota formulářového pole s přihlašovacím jménem uživatele.
    username: ''
  },
  // Výchozí jednotka úložiště
  storage: 'internal',
  // Výchozí média typ streamů, který se použije,
  // pokud nemá médium žádný typ definován.
  streamDefaultContainer: 'video/mp4',
  // Maximální počet časovačů, který je dovolen nastavit.
  // Na SSSP je počet omezen na 7 časovačů zapnutí a 7 časovačů vypnutí.
  // Časovače nad tento limit jsou ignorovány.
  // Na webOS je situace podobná a TV nedovolí nastavit více časovačů 7 + 7.
  // Při pokusu o nastavení dalšího časovače dojde k chybě při volání metody.
  // Informace o tomto limitu v dokumentaci chybí, ale byla potvrzena na fóru.
  timerMax: 7,
  // Prolínač médií.
  viewer: {
    // Zpoždění, které se aplikuje při detekovaném zacyklení. [ms]
    errorDelay: 1 * 1000,
    // Počet po sobě jdoucích selhaných médií, které se považují za zacyklení.
    errorLimit: 10,
    // Maximální počet médií, které se přeskočí při pokusu o přednačtení následujícího slajdu.
    // Po dosažení limitu se pokus o načtení zruší a následující médium přehraje bez přednačtení.
    // Čím větší hodnota, tím náročnější je vyhledání slajdu k načtení.
    // Pokud by bylo neomezeno, tak hrozí deadlock,
    // kdy se může hledat vhodné médium do nekonečna a nedojde k uvolnění přehrávaného zdroje.
    loadLimit: 10
  }
};
